import type { HeaderRow } from 'components/LoanPipelineHeaderConfigs/HeaderSelectionModal'
import { ProductTypes } from 'config'
import { AiEngine } from 'config/ai.type'
import type { NexysSharedDisplayConfig } from 'config/nexysShareFeatures'
import { IExtensionCost } from 'pages/LoanStructure/interfaces'
import { IRange } from 'pages/Resources'
import { LoanType } from 'reducers/loanDetail.reducer'

import { LeverageVisibleKeys } from './constants'

export interface IMinMaxLimitPerTypes {
  productType: string
  transactionType: string
  loanAmount: IRange
  minAmount: number
  maxAmount: number
  minPercent: number
  maxPercent: number
}

export interface IManualProgram {
  id: number
  programName: string
  rate: number
  price: number
  rateSpread: number
  exitFee: number
}

export interface IManualPricingSetting {
  productName: string
  interestOnly?: boolean
  lockTerm: number
  programs: IManualProgram[]
}

export interface ILTCFormula {
  loanAmountFactor: number
  constructionBudgetFactor: number
  interestReserveFactor: number
  loanAmountPercentageFactor: number
  fixedAmount: number
}

export interface ILTCFormulas {
  productType: string
  formula: ILTCFormula
}

export enum PricingRuleOptions {
  RTL_TRADER = 'rtlTrader',
  MANUAL_PRICING = 'manualPricing',
}

export interface ILoanGlobalSettings {
  minProposedLoanAmount: number
  maxProposedLoanAmount: number
  allowCentForLoanAmount: boolean
  minAsIsValue: number
  minAfterRepairValue: number
  minTotalConstructionFinanced: number
  maxTotalConstructionFinanced: number
  minConstructionFinancedHoldback: number
  maxConstructionFinancedHoldback: number
  maxPercentOfEstimatedClosingAmountToBorrower: number
  minCreditScore: number
  maxMultipleProperties: number
  minBrokerFeePercent: number
  maxBrokerFeePercent: number
  minBrokerFeeAmount: number
  commitmentFeeRequiredTypes: IMinMaxLimitPerTypes[]
  dueDilligenceRequiredTypes: IMinMaxLimitPerTypes[]
  defaultUnderwritingFee: number
  rateSheetTime: number
  priceDisplay: string
  appraisalStatisticsVisible: Record<string, boolean>
  showingTermSheetPlaceholders: string[]
  defaultChannel: LoanType
  manualPricingSetting: IManualPricingSetting
  lockExtensionCost: IExtensionCost
  nexysConnectionAdmin?: Record<string, boolean>
  nexysConnections?: Record<string, boolean>
  /** peerProjectId -> { [templateNo]: selected } — templates this company shares with that peer */
  nexysSharedTemplates?: Record<string, Record<string, boolean>>
  /** peerProjectId -> featureKey -> { enabled, pagePermissions } */
  nexysSharedDisplay?: NexysSharedDisplayConfig
  /** peerProjectId -> LoanType channel for inbound pushed loans */
  nexysPullChannel?: Record<string, LoanType>
  ui: Record<string, string>
  potentialDuplicateHeaders: HeaderRow[]
  pushToFinresi: boolean
  defaultAiEngine?: AiEngine
  defaultAiModel?: string
  invoiceAiAutofillEnabled?: boolean
  feeSheetAiAutofillEnabled?: boolean
  budgetAiAutofillEnabled?: boolean
  partyAiAutofillEnabled?: boolean
  insuranceAiAutofillEnabled?: boolean
  borrowerAiAutofillEnabled?: boolean
  creditScoreAiAutofillEnabled?: boolean
  secondaryMarketingAiAutofillEnabled?: boolean
  leverageVisible: Record<LeverageVisibleKeys, boolean>
  /** Company-wide alphanumeric prefix for displayed loan numbers (e.g. "D" → D1000). */
  loanNumberPrefix?: string
  ltcFormulas?: ILTCFormulas[]
  showPricingForBorrower?: boolean
  pricingRules: Record<ProductTypes, PricingRuleOptions>
}
